import { useState, useEffect } from "react";
import Slider from "react-slick";
import { Link } from "react-router-dom";
import {
  ChevronLeft,
  ChevronRight,
  Calendar,
  ExternalLink,
} from "lucide-react";
import api from "../../utils/axios.config";
import { baseUrl } from "../../utils/url";

// Custom arrow components for better styling - positioned far from text
const PrevArrow = (props) => {
  const { onClick } = props;
  return (
    <button
      className="absolute left-0 top-1/2 z-10 -translate-y-1/2 bg-white/80 p-2 rounded-full shadow-md hover:bg-white transition-all duration-200 focus:outline-none"
      onClick={onClick}
      aria-label="Previous slide"
    >
      <ChevronLeft size={24} className="text-gray-800" />
    </button>
  );
};

const NextArrow = (props) => {
  const { onClick } = props;
  return (
    <button
      className="absolute right-0 top-1/2 z-10 -translate-y-1/2 bg-white/80 p-2 rounded-full shadow-md hover:bg-white transition-all duration-200 focus:outline-none"
      onClick={onClick}
      aria-label="Next slide"
    >
      <ChevronRight size={24} className="text-gray-800" />
    </button>
  );
};

function Carousel() {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);

  const handleGetHeaderBanners = async () => {
    try {
      setLoading(true);
      const { data } = await api.get("header-banner");
      setBanners(data.data);
    } catch (error) {
      console.error("Failed to fetch banners:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    handleGetHeaderBanners();
  }, []);

  const settings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: true,
    autoplay: true,
    autoplaySpeed: 5000,
    pauseOnHover: true,
    nextArrow: <NextArrow />,
    prevArrow: <PrevArrow />,
    dotsClass: "slick-dots custom-dots",
    appendDots: (dots) => (
      <div className="bottom-6">
        <ul className="flex justify-center gap-2"> {dots} </ul>
      </div>
    ),
    customPaging: (i) => (
      <div className="w-3 h-3 bg-gray-300 rounded-full hover:bg-primary transition-all duration-200"></div>
    ),
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          arrows: true,
          dots: true,
        },
      },
      {
        breakpoint: 768,
        settings: {
          arrows: false,
          dots: true,
        },
      },
    ],
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64 md:h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="mx-2 sm:mx-4 md:mx-8 my-6 relative">
      <div className="rounded-xl overflow-hidden shadow-lg">
        <Slider {...settings}>
          {banners
            .slice()
            .reverse()
            .map((banner, index) => (
              <div key={index} className="outline-none focus:outline-none">
                <div className="bg-gradient-to-r from-gray-50 to-white">
                  <div className="flex flex-col lg:flex-row items-center px-4 sm:px-6 py-6 md:py-8">
                    {/* Content section */}
                    <div className="lg:w-1/2 lg:pr-12 text-left mb-6 lg:mb-0 z-0">
                      <h1 className="text-2xl sm:text-2xl md:text-3xl lg:text-4xl tracking-wide leading-relaxed font-bold text-gray-800 mb-4">
                        {banner?.title}
                      </h1>
                      <p className="text-gray-600 mb-6 text-sm md:text-base line-clamp-3 md:line-clamp-4">
                        {banner?.description}
                      </p>
                      <div className="flex flex-wrap gap-4">
                        <Link
                          to="/donation"
                          className="btn bg-primary hover:bg-primary/90 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-lg flex items-center gap-2 transition-all duration-200 shadow-md hover:shadow-lg"
                        >
                          Donate Now
                          <ExternalLink size={16} />
                        </Link>
                        <Link
                          to={`/about`}
                          className="btn border border-gray-300 hover:border-primary hover:text-primary px-4 sm:px-6 py-2 sm:py-3 rounded-lg transition-all duration-200"
                        >
                          Learn More
                        </Link>
                      </div>
                    </div>

                    {/* Image section */}
                    <div className="lg:w-1/2 mt-4 lg:mt-0">
                      <div className="overflow-hidden rounded-lg">
                        <img
                          src={`${baseUrl}${banner?.imageUrl}`}
                          className="w-full rounded-lg h-48 sm:h-56 md:h-72 lg:h-80 object-cover shadow-md transition-transform duration-300 hover:scale-105"
                          alt={banner?.title || "Campaign banner"}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
        </Slider>
      </div>
    </div>
  );
}

export default Carousel;
